import { useCallback, useEffect, useState } from "react";

import { usePromise } from "react-use";

import { useCart } from "./useCart";
import { useDefinition } from "./useDefinition";
import { useMessage } from "./useMessage";

import { useClient } from "./useClient";
import { useErrorHandling } from "./useErrorHandling";
import { useAppContext } from "./useAppContext";

export function usePrice({
	busyBlocker,
}: {
	busyBlocker?: eXpress.BalancedScope;
} = {}) {
	// TODO:
	// Use hooks + useQuery

	const { platform } = useAppContext();
	const client = useClient();

	const ifMounted = usePromise();

	const { createErrorHandler } = useErrorHandling();
	const cart = useCart();
	const definition = useDefinition();
	const [price, setPrice] = useState<undefined | eXpress.core.IPrice>(undefined);

	const getPrice = useCallback(() => {
		if (!definition || !cart) {
			setPrice(undefined);
			return;
		}

		const productVariant = definition.getSelectedProductVariant();

		const variableAmount = definition.getUsedPages(
			(page) => page.spread.kind !== eXpress.core.SpreadKind.Cover,
		).length;

		let activeOptionCodes: eXpress.ProductOptionCode[] = [];
		if (definition.activeOptions) {
			activeOptionCodes = definition.activeOptions.map((o) => o.code);
		} else {
			// Temporary
			if (definition.selectedProductOptionCode)
				activeOptionCodes.push(definition.selectedProductOptionCode);
			platform.queryString
				.getQueryStringParameter("options")
				?.split(",")
				.map((o) => eXpress.ProductOptionCode.fromString(o.trim()))
				.forEach((o) => activeOptionCodes.push(o));
		}

		const allowedAttributeCodes = definition.product.attributes
			? definition.product.attributes.keys
			: undefined;

		const promise = client.getPriceAsync(
			cart,
			productVariant.code,
			variableAmount,
			activeOptionCodes,
			allowedAttributeCodes,
		);

		ifMounted(busyBlocker ? busyBlocker.while(promise) : promise)
			.then(setPrice)
			.catch((error: unknown) => {
				setPrice(undefined);
				createErrorHandler()(error);
			});
	}, [
		definition,
		cart,
		client,
		ifMounted,
		busyBlocker,
		platform.queryString,
		createErrorHandler,
	]);

	useEffect(() => getPrice(), [getPrice]);

	useMessage((message) => {
		if (eXpress.core.shouldUpdatePrice(message)) {
			getPrice();
		}
	});

	return price;
}
